import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { useI18n } from '../../i18n'
import { componentScope } from '../../lib/cssScope'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { isScannableAssetType, normalizeAssetType, assetTypeI18nKey } from '../../constants/assetTaxonomy'
import { formatAssetTargetLabel } from '../../utils/gvmHosts'

interface Asset {
  id: string
  name: string
  type: string
  environment: string
  ipAddress: string
  complianceScore: number
  riskLevel: string
  vulnerabilities: {
    critical: number
    high: number
    medium: number
    low: number
    total: number
  }
  criticality?: string
  riskScore?: number
  owner?: {
    id: string
    name: string
    department?: string
  }
  lastScan?: string
}

interface AssetsInventoryTableProps {
  assets: Asset[]
  selectedIds: Set<string>
  onSelectAsset: (assetId: string, selected: boolean) => void
  onView?: (assetId: string) => void
  onEdit: (asset: Asset) => void
  onDelete: (assetId: string) => void
  onScan: (asset: Asset) => void
  getRiskLevelColor: (riskLevel: string | undefined | null) => string
  getRiskLevelIcon: (riskLevel: string | undefined | null) => React.ReactNode
}

const AssetsInventoryTable: React.FC<AssetsInventoryTableProps> = ({
  assets,
  selectedIds,
  onSelectAsset,
  onView,
  onEdit,
  onDelete,
  onScan,
  getRiskLevelColor,
  getRiskLevelIcon
}) => {
  const { t } = useI18n()
  const navigate = useNavigate()

  const getCriticalityColor = (criticality?: string) => {
    if (!criticality) return 'bg-gray-50 text-gray-800'
    const crit = criticality.toLowerCase()
    switch (crit) {
      case 'critical':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-50 text-orange-700'
      case 'medium':
        return 'bg-yellow-50 text-yellow-700'
      case 'low':
        return 'bg-green-50 text-green-700'
      default:
        return 'bg-gray-50 text-gray-700'
    }
  }

  const getRiskScoreColor = (score?: number) => {
    if (score === undefined || score === null) return 'text-gray-600'
    if (score >= 75) return 'text-red-600'
    if (score >= 50) return 'text-orange-600'
    if (score >= 25) return 'text-yellow-600'
    return 'text-green-600'
  }

  return (
    <div className={`${componentScope('AssetsInventoryTable')} ${listStyles.tableWrap}`}>
      <table className={listStyles.table}>
        <thead>
          <tr>
            <th className={listStyles.th} scope="col" aria-label={t('assets.inventory.select', 'Select')} />
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.name', 'Name')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.type', 'Type')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.owner', 'Owner')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.environment', 'Environment')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.criticality', 'Criticality')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.riskScore', 'Risk Score')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.totalVulnerabilities', 'Total Vulnerabilities')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.complianceScore', 'Compliance Score')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.lastScan', 'Last Scan')}
            </th>
            <th className={listStyles.th} scope="col">
              {t('assets.inventory.table.actions', 'Actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr key={asset.id} className={listStyles.row}>
              <td className={listStyles.td}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(asset.id)}
                  onChange={(e) => onSelectAsset(asset.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-[var(--ds-primary,#2563eb)]"
                  aria-label={t('assets.inventory.selectRow', 'Select row')}
                />
              </td>
              <td className={`${listStyles.td} ${listStyles.cellStrong} ${listStyles.firstCell}`}>
                <div>{asset.name}</div>
                {(() => {
                  const target = formatAssetTargetLabel(asset)
                  return target ? (
                    <div className="mt-0.5 text-xs font-normal text-[var(--ds-text-muted,#64748b)]" title={target}>
                      {target}
                    </div>
                  ) : null
                })()}
              </td>
              <td className={listStyles.td}>
                <span className={`${listStyles.badge} ${listStyles.badgeInProgress}`} title={asset.type}>
                  {t(assetTypeI18nKey(asset.type), normalizeAssetType(asset.type).replace(/_/g, ' '))}
                </span>
              </td>
              <td className={`${listStyles.td} ${listStyles.cellMuted}`}>{asset.owner?.name || '—'}</td>
              <td className={listStyles.td}>
                <span className={`${listStyles.badge} ${listStyles.badgeDefault}`}>{asset.environment || 'UNKNOWN'}</span>
              </td>
              <td className={listStyles.td}>
                <span className={`rounded px-2 py-1 text-xs font-medium ${getCriticalityColor(asset.criticality)}`}>
                  {asset.criticality || 'Unknown'}
                </span>
              </td>
              <td className={listStyles.td}>
                <span className={`font-medium ${getRiskScoreColor(asset.riskScore)}`}>
                  {asset.riskScore !== undefined && asset.riskScore !== null ? `${asset.riskScore.toFixed(1)}%` : '—'}
                </span>
              </td>
              <td className={listStyles.td}>
                <span
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${getRiskLevelColor(asset.riskLevel)}`}
                >
                  {getRiskLevelIcon(asset.riskLevel)}
                  {asset.vulnerabilities?.total ?? 0}
                </span>
              </td>
              <td className={listStyles.td}>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 w-24 max-w-full rounded-full bg-[var(--ds-gray-200,#e5e7eb)]"
                    role="presentation"
                  >
                    <div
                      className="h-2 rounded-full bg-[var(--ds-primary,#2563eb)] transition-all"
                      style={{ width: `${Math.min(100, Math.max(0, asset.complianceScore))}%` }}
                    />
                  </div>
                  <span className="text-sm tabular-nums">{asset.complianceScore}%</span>
                </div>
              </td>
              <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                {asset.lastScan ? new Date(asset.lastScan).toLocaleDateString() : '—'}
              </td>
              <td className={listStyles.td}>
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() => (onView ? onView(asset.id) : navigate(`/assets/${asset.id}`))}
                    className="!border-transparent !bg-transparent !px-1 !py-0 !text-xs !text-[var(--ds-primary,#2563eb)] !shadow-none hover:!underline"
                  >
                    {t('common.view', 'View')}
                  </Button>
                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() => onEdit(asset)}
                    className="!border-transparent !bg-transparent !px-1 !py-0 !text-xs !text-[var(--ds-primary,#2563eb)] !shadow-none hover:!underline"
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                  {isScannableAssetType(asset.type) ? (
                    <Button
                      type="button"
                      color="outline"
                      size="small"
                      onClick={() => onScan(asset)}
                      className="!border-transparent !bg-transparent !px-1 !py-0 !text-xs !text-emerald-600 !shadow-none hover:!underline"
                    >
                      {t('assets.inventory.scan', 'Scan')}
                    </Button>
                  ) : (
                    <span className="text-xs text-gray-400 italic">{t('assets.inventory.notScannable', 'Not scannable')}</span>
                  )}
                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() => onDelete(asset.id)}
                    className="!border-transparent !bg-transparent !px-1 !py-0 !text-xs !text-red-600 !shadow-none hover:!underline"
                  >
                    {t('common.delete', 'Delete')}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AssetsInventoryTable
